import { api, apiBlob } from '../../../lib/api';
import type { ListQuery, PagedResult } from '../users/userTypes';
import type { FileCategory, StoredFileListItem } from './fileTypes';

function toQueryString(query: ListQuery): string {
  const params = new URLSearchParams();

  if (query.search?.trim()) params.set('search', query.search.trim());
  if (query.page && query.page > 1) params.set('page', String(query.page));
  if (query.pageSize) params.set('pageSize', String(query.pageSize));
  if (query.sort) params.set('sort', query.sort);
  /*
    Facets are sent whenever the key is present, empty string included — an empty value is the
    reader saying "none of these", which matches no row. A truthiness check here would drop it
    and the server would read the axis as unmentioned.
  */
  if (query.status !== undefined) params.set('status', query.status);

  const serialized = params.toString();
  return serialized ? `?${serialized}` : '';
}

export const filesApi = {
  /** Soft-deletes every record in the module at once; resolves with how many were deleted. */
  clear: () => api<number>('/api/files/clear', { method: 'POST' }),

  list: (query: ListQuery, signal?: AbortSignal) =>
    api<PagedResult<StoredFileListItem>>(`/api/files${toQueryString(query)}`, { signal }),

  get: (id: string, signal?: AbortSignal) => api<StoredFileListItem>(`/api/files/${id}`, { signal }),

  /**
   * The upload targets and their limits, straight from the server's registry. The form
   * reads its accept list and size cap from here rather than hard-coding them, so the
   * client can never offer something the server would refuse.
   */
  categories: (signal?: AbortSignal) => api<FileCategory[]>('/api/files/categories', { signal }),

  upload: (category: string, file: File) => {
    const form = new FormData();
    form.append('category', category);
    form.append('file', file);

    return api<StoredFileListItem>('/api/files', { method: 'POST', body: form });
  },

  /** The decrypted file. The server reassembles both parts and verifies the hash first. */
  download: (id: string) => apiBlob(`/api/files/${id}/content`),

  /** Soft delete. The encrypted parts stay on disk and the file can be restored. */
  deactivate: (id: string) => api<void>(`/api/files/${id}`, { method: 'DELETE' }),

  activate: (id: string) => api<void>(`/api/files/${id}/restore`, { method: 'POST' }),
};
